// 🔧 school_election_playbook.js
// ✅ Purpose: Public-facing event playbook for running school elections with Let's Go Vote
// ✅ Features:
// - Explains school elections, student voting, candidates, ranked choice, and trusted results
// - Public and SEO-friendly
// - Uses the Dashboard-style LGV layout
// - Helps prospective customers understand the school election workflow
// - Fully block-commented for clarity and future devs

import React from "react";
import { Link } from "react-router-dom";
import SeoHead from "../../../components/seo_head";

function SchoolElectionPlaybook() {
  return (
    <section id="top-content" className="container-fluid py-5">
      <SeoHead
        title="School Election Playbook | Let's Go Vote"
        description="Learn how to run school elections, student council votes, club elections, candidate voting, and ranked-choice ballots using Let's Go Vote."
        path="/guides/school-election-playbook"
        breadcrumbs={[
          { name: "Home", path: "/" },
          { name: "Guides", path: "/guides" },
          {
            name: "School Election Playbook",
            path: "/guides/school-election-playbook",
          },
        ]}
      />
      <div className="row mb-4">
        <div className="col text-start">
          <h1 className="display-5">🏫 School Election Playbook</h1>
          <p className="lead text-muted">
            Learn how to run student elections, club votes, candidate contests,
            and ranked-choice ballots.
          </p>
        </div>
      </div>

      <div className="white-section p-4 rounded shadow-sm bg-white">
        <section className="mb-5">
          <div className="alert alert-info text-start">
            <h5 className="mb-3">Simple voting for school communities</h5>
            <p className="mb-0">
              Let&apos;s Go Vote can help schools and clubs organize candidates,
              voting categories, QR access, ballots, audit review, and results
              publishing.
            </p>
          </div>
        </section>

        <section className="mb-5">
          <h3 className="mb-3">Step 1: Create the election event</h3>
          <p>
            Start by creating an event for the election, club vote, student
            council race, class competition, or school-wide decision. Add the
            event description, dates, voting window, and public instructions.
          </p>
        </section>

        <section className="mb-5">
          <h3 className="mb-3">Step 2: Choose the right voting model</h3>
          <p>
            Simple candidate races may use People&apos;s Choice Voting. Elections
            where voters rank preferences can use Ranked Choice Voting. Judged
            scoring can be used for competitions where staff or judges score
            entries.
          </p>

          <Link
            to="/guides/configure-voting"
            className="btn btn-sm btn-outline-primary"
          >
            Read the Voting Guide
          </Link>
        </section>

        <section className="mb-5">
          <h3 className="mb-3">Step 3: Create offices or ballot categories</h3>
          <p>
            Categories can represent offices, contests, or ballot questions.
            Examples include Student Body President, Treasurer, Spirit Week
            Theme, Club Officer, Favorite Project, or Best Presentation.
          </p>
        </section>

        <section className="mb-5">
          <h3 className="mb-3">Step 4: Add candidates or choices</h3>
          <p>
            Entries can represent candidates, teams, ballot choices, projects,
            or presentations. Each entry can include a name, description, photo,
            campaign statement, or supporting details.
          </p>

          <Link
            to="/guides/categories-and-entries"
            className="btn btn-sm btn-outline-primary"
          >
            Read Categories & Entries
          </Link>
        </section>

        <section className="mb-5">
          <h3 className="mb-3">Step 5: Share voting access</h3>
          <p>
            Schools can share a direct event link or QR code. QR codes can be
            posted in classrooms, assemblies, newsletters, or event materials so
            voters can open the ballot quickly.
          </p>

          <Link to="/guides/qr-codes" className="btn btn-sm btn-outline-primary">
            Read the QR Code Guide
          </Link>
        </section>

        <section className="mb-5">
          <h3 className="mb-3">Step 6: Review results and publish winners</h3>
          <p>
            After voting closes, managers can review results and audit activity
            before winners are announced. This helps build confidence in the
            final outcome.
          </p>

          <Link
            to="/guides/security-and-trust"
            className="btn btn-sm btn-outline-primary"
          >
            Read Security & Trust
          </Link>
        </section>

        <section className="mb-0">
          <div className="alert alert-primary text-start mb-0">
            <h5 className="mb-3">Planning a school election?</h5>
            <p className="mb-3">
              Let&apos;s Go Vote can help schools manage ballots, candidates, QR
              voting, ranked-choice voting, and trusted results.
            </p>

            <Link to="/request_event" className="btn btn-primary">
              Request an Event
            </Link>
          </div>
        </section>
      </div>
    </section>
  );
}

export default SchoolElectionPlaybook;